import { useCallback, useEffect, useMemo, useState } from 'react'
import type * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineSceneryIcon from 'LuxKit/Icons/line/LineSceneryIcon'
import type { DrawImagesCategory } from 'tripPlanner/hooks/api/locationImage'
import { useLocationDrawPlaceDetails } from 'tripPlanner/hooks/api/locationImage'
import type { CardOption } from './CardsList'
import { imageCardFrameStyles } from './ImageCardFrame'

const ImageContainer = styled.div`
  ${imageCardFrameStyles}
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const ImageInner = styled.div`
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FallbackImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.four};
`

const NoSearchFallbackContainer = styled.div`
  ${imageCardFrameStyles}
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.four};
`

interface Props {
  option: CardOption
  category: DrawImagesCategory
  optionIndex?: number
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const InteractiveImageContainer = styled.button.attrs({ type: 'button' })`
  ${imageCardFrameStyles}
  position: relative;
  padding: 0;
  border: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  cursor: pointer;
`

function ImageCard({ option, category, optionIndex, onClick }: Props) {
  const intl = useIntl()
  const [imageError, setImageError] = useState(false)
  const locationData = useMemo(() => {
    const placeSearch = option.placeSearch?.trim()
    if (!placeSearch) return undefined
    return {
      title: option.title,
      description: option.description,
      emoji: option.emoji,
      placeSearch,
    }
  }, [option])

  const { data: drawData, isLoading } = useLocationDrawPlaceDetails(locationData, category)
  const firstImageId = drawData?.imageIdentifiers?.[0]

  useEffect(() => {
    setImageError(false)
  }, [firstImageId])

  const handleImageError = useCallback(() => {
    setImageError(true)
  }, [])
  const isUrl = firstImageId?.startsWith('http')

  const renderImageWrapper = (children: React.ReactNode) => {
    if (onClick) {
      return (
        <InteractiveImageContainer
          onClick={onClick}
          data-option-index={optionIndex}
          aria-label={intl.formatMessage(
            {
              defaultMessage: 'Open {title} details',
              id: 'Pmw_RwwOtjqvS9LFYyYu',
              description: 'Aria label for opening trip planner place details from an image card',
            },
            { title: option.title },
          )}
        >
          {children}
        </InteractiveImageContainer>
      )
    }

    return (
      <ImageContainer>
        {children}
      </ImageContainer>
    )
  }

  if (!locationData) {
    return (
      <NoSearchFallbackContainer>
        <LineSceneryIcon size={40} />
      </NoSearchFallbackContainer>
    )
  }

  if (isLoading) {
    return renderImageWrapper(<LoadingBox floating />)
  }

  if (firstImageId && !imageError) {
    if (isUrl) {
      return renderImageWrapper(
        <ImageInner>
          <img src={firstImageId} alt={option.title} onError={handleImageError} />
        </ImageInner>,
      )
    }
    return renderImageWrapper(
      <ImageInner>
        <ResponsiveImage
          id={firstImageId}
          aspectRatio="5:3"
          fit="center"
          alt={option.title}
          onError={handleImageError}
        />
      </ImageInner>,
    )
  }

  return renderImageWrapper(
    <FallbackImageContainer>
      <LineSceneryIcon size={40} />
    </FallbackImageContainer>,
  )
}

export default ImageCard
